import type { ScenarioDef, SpawnEvent } from "../data/scenario";
import type { CampaignDifficulty } from "./types";

function scaleSpawns(spawns: SpawnEvent[], timeMul: number): SpawnEvent[] {
  return spawns.map((s) => ({
    ...s,
    at: Math.round(s.at * timeMul * 10) / 10,
  }));
}

/**
 * Easy: at most one mid-script echo in a wide quiet gap so openers aren't
 * pure single-file clears. Keeps teaching room; doesn't stack Hard-style.
 */
function densifyEasySpawns(
  spawns: SpawnEvent[],
  duration: number,
): SpawnEvent[] {
  if (spawns.length < 3) return spawns.map((s) => ({ ...s }));
  const sorted = [...spawns].sort((a, b) => a.at - b.at);
  // Gaps ≥ 11s: fill up to two so concurrent pressure shows without Hard densify
  const extras: SpawnEvent[] = [];
  for (let i = 0; i < sorted.length - 1 && extras.length < 2; i++) {
    const a = sorted[i]!;
    const b = sorted[i + 1]!;
    const gap = b.at - a.at;
    if (gap < 11) continue;
    const at = Math.round((a.at + gap * 0.5) * 10) / 10;
    if (duration > 0 && at >= duration - 3) continue;
    if (
      sorted.some((s) => Math.abs(s.at - at) < 2) ||
      extras.some((s) => Math.abs(s.at - at) < 2)
    ) {
      continue;
    }
    extras.push({
      ...a,
      at,
      label: a.label ? `${a.label} · again` : undefined,
    });
  }
  // Mild opening double so the first Fear isn't free forever on Easy
  if (sorted.length >= 6) {
    const first = sorted[0]!;
    const secondAt = Math.round((first.at + 4.5) * 10) / 10;
    const next = sorted[1];
    if (
      (!next || next.at - first.at >= 10) &&
      !extras.some((s) => Math.abs(s.at - secondAt) < 2)
    ) {
      extras.push({
        ...first,
        at: secondAt,
        label: first.label ? `${first.label} · swell` : "Pressure swell",
      });
    }
  }

  if (!extras.length) return sorted.map((s) => ({ ...s }));
  return [...sorted, ...extras].sort((x, y) => x.at - y.at);
}

/**
 * Hard: fill large quiet gaps with echo spawns so sparse openers actually
 * stack pressure. Dense late scripts (small gaps) are barely touched.
 *
 * Soft / epilogue stages (short duration or high integrity budget) only get
 * mild compression — no gap-fill or opening double-tap — so celebration beats
 * stay celebratory even on Hard.
 */
function densifyHardSpawns(
  spawns: SpawnEvent[],
  duration: number,
  opts: { softStage: boolean },
): SpawnEvent[] {
  if (spawns.length < 2) return spawns.map((s) => ({ ...s }));
  // Epilogue / breathing room stages: keep authored count, only time-scaled.
  if (opts.softStage) return spawns.map((s) => ({ ...s }));

  const sorted = [...spawns].sort((a, b) => a.at - b.at);
  const extras: SpawnEvent[] = [];

  // Wide-gap echoes — primary densify for early Mind-only stages
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]!;
    const b = sorted[i + 1]!;
    const gap = b.at - a.at;
    if (gap < 9) continue;
    const at = Math.round((a.at + gap * 0.48) * 10) / 10;
    if (duration > 0 && at >= duration - 3) continue;
    // Avoid stacking on top of another event
    if (sorted.some((s) => Math.abs(s.at - at) < 1.2)) continue;
    if (extras.some((s) => Math.abs(s.at - at) < 1.2)) continue;
    extras.push({
      ...a,
      at,
      label: a.label ? `${a.label} · again` : undefined,
    });
  }

  // Opening double-tap on sparse combat scripts: second lie arrives before
  // first is fully settled so Hard openers can't single-file clear forever.
  if (sorted.length <= 8 && sorted.length >= 4) {
    const first = sorted[0]!;
    const secondAt = first.at + 3.2;
    const next = sorted[1];
    if (
      (!next || next.at - first.at >= 8) &&
      !extras.some((s) => Math.abs(s.at - secondAt) < 1.5)
    ) {
      extras.push({
        ...first,
        at: Math.round(secondAt * 10) / 10,
        label: first.label ? `${first.label} · swell` : "Pressure swell",
      });
    }
  }

  return [...sorted, ...extras].sort((a, b) => a.at - b.at);
}

/**
 * Easy: more integrity, slower pressure, slightly longer mission.
 * Hard: less integrity, denser pressure (compressed + gap-fill echoes),
 * slightly shorter timer.
 * Standard: authored values as-is.
 *
 * Endless free play: no duration win — only integrity / spawn script timing scale.
 */
export function scaleScenario(
  base: ScenarioDef,
  difficulty: CampaignDifficulty,
): ScenarioDef {
  const endless = base.mode === "endless";

  if (difficulty === "standard") {
    return {
      ...base,
      spawns: base.spawns.map((s) => ({ ...s })),
      escalation: base.escalation
        ? {
            ...base.escalation,
            leadIn: base.escalation.leadIn,
            strategies: base.escalation.strategies.map((st) => ({
              ...st,
              pool: st.pool.map((p) => ({ ...p })),
            })),
          }
        : undefined,
    };
  }

  if (difficulty === "easy") {
    // Stay close to authored density so Guard Prayers still matter.
    // Cushion comes from integrity + slower enemy pace (Game) + no Confusion,
    // not from deleting half the script.
    const timeMul = endless ? 1.1 : 1.1;
    let spawns = scaleSpawns(base.spawns, timeMul);
    const duration = endless ? 0 : Math.round(base.duration * timeMul);

    // Sparse openers: one mid-gap echo so single-file random spam is punished,
    // but still gentler than Hard densify (no opening double-tap).
    // Endless free-play open gets the same light swell so Easy is not a cruise.
    if (
      (endless && base.spawns.length >= 8) ||
      (!endless && base.spawns.length >= 6 && base.spawns.length <= 9)
    ) {
      spawns = densifyEasySpawns(spawns, duration);
    }

    // Soft epilogues: drop only the final beat so celebration stays light
    if (!endless && base.duration > 0 && base.duration <= 75) {
      const cutAt = duration * 0.9;
      const cut = spawns.filter((s) => s.at <= cutAt);
      if (cut.length) spawns = cut;
    }

    return {
      ...base,
      id: `${base.id}-easy`,
      name: `${base.name} (Easy)`,
      duration,
      maxIntegrity: Math.round(base.maxIntegrity * 1.15),
      spawns,
      // Always kinder on Easy even if base re-enables confusion later
      disableConfusion: true,
      escalation: base.escalation
        ? {
            ...base.escalation,
            leadIn: (base.escalation.leadIn ?? 3) * (endless ? 1.06 : 1.15),
            strategies: base.escalation.strategies.map((st) => ({
              ...st,
              duration: st.duration * (endless ? 1.04 : 1.08),
              baseInterval: st.baseInterval * (endless ? 1.04 : 1.15),
              pool: st.pool.map((p) => ({ ...p })),
            })),
          }
        : undefined,
    };
  }

  // hard — compress timeline, then fill remaining quiet gaps so openers stack
  const timeMul = endless ? 0.88 : 0.78;
  const scaled = scaleSpawns(base.spawns, timeMul);
  const duration = endless
    ? 0
    : Math.max(70, Math.round(base.duration * 0.9));
  // Soft: short epilogues / celebration beats (not high-integrity combat openers)
  const softStage =
    !endless &&
    (base.duration <= 75 ||
      (base.spawns.length <= 5 && !!base.disableConfusion));
  const spawns = endless
    ? densifyHardSpawns(scaled, 0, { softStage: false })
    : densifyHardSpawns(scaled, duration, { softStage });

  return {
    ...base,
    id: `${base.id}-hard`,
    name: `${base.name} (Hard)`,
    duration,
    // Soft stages keep more of their integrity cushion on Hard.
    // Endless Hard: slightly more HP than campaign Hard so hold-will can
    // survive the first protocols under pressure (wipe is the next cycle).
    maxIntegrity: Math.max(
      48,
      Math.round(
        base.maxIntegrity *
          (endless ? 0.9 : softStage ? 0.88 : 0.78),
      ),
    ),
    spawns,
    // Hard reintroduces Confusion (residuals can twist)
    disableConfusion: false,
    escalation: base.escalation
      ? {
          ...base.escalation,
          leadIn: Math.max(1.2, (base.escalation.leadIn ?? 3) * 0.8),
          strategies: base.escalation.strategies.map((st) => ({
            ...st,
            duration: st.duration * (endless ? 0.94 : 0.9),
            baseInterval: st.baseInterval * (endless ? 0.95 : 0.82),
            pool: st.pool.map((p) => ({ ...p })),
          })),
        }
      : undefined,
  };
}
